import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { parseJobApplicantError } from 'api/requests/job-applicant-link-requests';
import {
  discardApplicantRequest,
  downloadApplicantCvRequest,
  getApplicantDetailRequest,
  getApplicantsRequest,
  promoteApplicantRequest,
  restoreApplicantRequest,
  updateApplicantBranchesRequest,
} from 'api/requests/job-applicant-requests';
import {
  JOB_APPLICANTS__CLEAR_APPLICANT_DETAIL,
  JOB_APPLICANTS__CLEAR_APPLICANTS,
  JOB_APPLICANTS__DISCARD_APPLICANT,
  JOB_APPLICANTS__DOWNLOAD_CV,
  JOB_APPLICANTS__GET_APPLICANT_DETAIL,
  JOB_APPLICANTS__GET_APPLICANTS,
  JOB_APPLICANTS__PATCH_APPLICANT_LOCATIONS,
  JOB_APPLICANTS__PATCH_APPLICANT_STATUS,
  JOB_APPLICANTS__PROMOTE_APPLICANT,
  JOB_APPLICANTS__REMOVE_APPLICANT,
  JOB_APPLICANTS__RESTORE_APPLICANT,
  JOB_APPLICANTS__SET_APPLICANT_ACTION_ERROR,
  JOB_APPLICANTS__SET_APPLICANT_ACTION_LOADING,
  JOB_APPLICANTS__SET_APPLICANT_DETAIL,
  JOB_APPLICANTS__SET_APPLICANT_DETAIL_ERROR,
  JOB_APPLICANTS__SET_APPLICANT_DETAIL_LOADING,
  JOB_APPLICANTS__SET_APPLICANTS,
  JOB_APPLICANTS__SET_APPLICANTS_ERROR,
  JOB_APPLICANTS__SET_APPLICANTS_LOADING,
  JOB_APPLICANTS__SET_DOWNLOAD_CV_ERROR,
  JOB_APPLICANTS__SET_PROMOTE_SUCCESS_MESSAGE,
  JOB_APPLICANTS__UPDATE_APPLICANT_BRANCHES,
} from 'store/constants';
import {
  APPLICANTS__PROMOTE_SUCCESS_BRANCHES_APPENDED,
  APPLICANTS__PROMOTE_SUCCESS_INVITATION_REUSED,
  APPLICANTS__PROMOTE_SUCCESS_NEW_INVITATION,
  APPLICANTS__PROMOTE_SUCCESS_PARTIAL_OVERLAP,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import {
  ApplicantStatus,
  IApplicantDetail,
  IApplicantListParams,
  IApplicantListResponse,
  IPromoteApplicantResponse,
} from 'types/employee/IJobApplicant';
import logError from 'utils/log-error';

export const setApplicantsAction = createAction<IApplicantListResponse>(JOB_APPLICANTS__SET_APPLICANTS);
export const setApplicantsLoadingAction = createAction<boolean>(JOB_APPLICANTS__SET_APPLICANTS_LOADING);
export const setApplicantsErrorAction = createAction<string | null>(JOB_APPLICANTS__SET_APPLICANTS_ERROR);
export const clearApplicantsAction = createAction(JOB_APPLICANTS__CLEAR_APPLICANTS);

export const setApplicantDetailAction = createAction<IApplicantDetail | null>(JOB_APPLICANTS__SET_APPLICANT_DETAIL);
export const setApplicantDetailLoadingAction = createAction<boolean>(JOB_APPLICANTS__SET_APPLICANT_DETAIL_LOADING);
export const setApplicantDetailErrorAction = createAction<string | null>(JOB_APPLICANTS__SET_APPLICANT_DETAIL_ERROR);
export const clearApplicantDetailAction = createAction(JOB_APPLICANTS__CLEAR_APPLICANT_DETAIL);

export const setApplicantActionLoadingAction = createAction<boolean>(JOB_APPLICANTS__SET_APPLICANT_ACTION_LOADING);
export const setApplicantActionErrorAction = createAction<string | null>(JOB_APPLICANTS__SET_APPLICANT_ACTION_ERROR);
export const patchApplicantStatusAction = createAction<{ id: number; status: ApplicantStatus }>(
  JOB_APPLICANTS__PATCH_APPLICANT_STATUS,
);
export const patchApplicantLocationsAction = createAction<{ id: number; locationIds: number[] }>(
  JOB_APPLICANTS__PATCH_APPLICANT_LOCATIONS,
);
export const removeApplicantAction = createAction<{ id: number }>(JOB_APPLICANTS__REMOVE_APPLICANT);
export const setPromoteSuccessMessageAction = createAction<string | null>(JOB_APPLICANTS__SET_PROMOTE_SUCCESS_MESSAGE);

export const downloadApplicantCvErrorAction = createAction<boolean>(JOB_APPLICANTS__SET_DOWNLOAD_CV_ERROR);

export const getApplicantsAction = createAsyncThunk(
  JOB_APPLICANTS__GET_APPLICANTS,
  async (params: IApplicantListParams, thunkApi) => {
    thunkApi.dispatch(setApplicantsLoadingAction(true));
    thunkApi.dispatch(setApplicantsErrorAction(null));
    try {
      const response = await getApplicantsRequest(params);
      thunkApi.dispatch(setApplicantsAction(response));
    } catch (error) {
      logError(error);
      // Translate the stable `error` key; `message` is null unless a backend translation exists.
      const { key } = parseJobApplicantError(error);
      thunkApi.dispatch(setApplicantsErrorAction(key ?? GENERAL__ERROR_MESSAGE));
    }
    thunkApi.dispatch(setApplicantsLoadingAction(false));
  },
);

/** Resets the action loading/error state too, so a stale result from a previous applicant's
 * modal never leaks into a freshly opened one. */
export const getApplicantDetailAction = createAsyncThunk(
  JOB_APPLICANTS__GET_APPLICANT_DETAIL,
  async (params: { companyId: number; id: number }, thunkApi) => {
    thunkApi.dispatch(setApplicantDetailLoadingAction(true));
    thunkApi.dispatch(setApplicantDetailErrorAction(null));
    thunkApi.dispatch(setApplicantActionErrorAction(null));
    try {
      const detail = await getApplicantDetailRequest(params.companyId, params.id);
      thunkApi.dispatch(setApplicantDetailAction(detail));
    } catch (error) {
      logError(error);
      const { key } = parseJobApplicantError(error);
      thunkApi.dispatch(setApplicantDetailErrorAction(key ?? GENERAL__ERROR_MESSAGE));
    }
    thunkApi.dispatch(setApplicantDetailLoadingAction(false));
  },
);

/** Never rejects — resolves `true`/`false` so callers can `.unwrap()` it to decide whether to
 * close the modal, while the error itself is still readable from `applicantActionError`. */
export const discardApplicantAction = createAsyncThunk(
  JOB_APPLICANTS__DISCARD_APPLICANT,
  async (params: { companyId: number; id: number }, thunkApi): Promise<boolean> => {
    thunkApi.dispatch(setApplicantActionLoadingAction(true));
    thunkApi.dispatch(setApplicantActionErrorAction(null));
    let success = false;
    try {
      await discardApplicantRequest(params.companyId, params.id);
      thunkApi.dispatch(patchApplicantStatusAction({ id: params.id, status: ApplicantStatus.Discarded }));
      success = true;
    } catch (error) {
      logError(error);
      const { key } = parseJobApplicantError(error);
      thunkApi.dispatch(setApplicantActionErrorAction(key ?? GENERAL__ERROR_MESSAGE));
    }
    thunkApi.dispatch(setApplicantActionLoadingAction(false));
    return success;
  },
);

export const restoreApplicantAction = createAsyncThunk(
  JOB_APPLICANTS__RESTORE_APPLICANT,
  async (params: { companyId: number; id: number }, thunkApi): Promise<boolean> => {
    thunkApi.dispatch(setApplicantActionLoadingAction(true));
    thunkApi.dispatch(setApplicantActionErrorAction(null));
    let success = false;
    try {
      await restoreApplicantRequest(params.companyId, params.id);
      thunkApi.dispatch(patchApplicantStatusAction({ id: params.id, status: ApplicantStatus.Submitted }));
      success = true;
    } catch (error) {
      logError(error);
      const { key } = parseJobApplicantError(error);
      thunkApi.dispatch(setApplicantActionErrorAction(key ?? GENERAL__ERROR_MESSAGE));
    }
    thunkApi.dispatch(setApplicantActionLoadingAction(false));
    return success;
  },
);

/** Wholesale-replaces the applicant's branch set — mirrors the PATCH contract, which does not
 * append. Never rejects, same `.unwrap()`-a-boolean shape as discard/restore. */
export const updateApplicantBranchesAction = createAsyncThunk(
  JOB_APPLICANTS__UPDATE_APPLICANT_BRANCHES,
  async (params: { companyId: number; id: number; locationIds: number[] }, thunkApi): Promise<boolean> => {
    thunkApi.dispatch(setApplicantActionLoadingAction(true));
    thunkApi.dispatch(setApplicantActionErrorAction(null));
    let success = false;
    try {
      await updateApplicantBranchesRequest(params.companyId, params.id, params.locationIds);
      thunkApi.dispatch(patchApplicantLocationsAction({ id: params.id, locationIds: params.locationIds }));
      success = true;
    } catch (error) {
      logError(error);
      const { key } = parseJobApplicantError(error);
      thunkApi.dispatch(setApplicantActionErrorAction(key ?? GENERAL__ERROR_MESSAGE));
    }
    thunkApi.dispatch(setApplicantActionLoadingAction(false));
    return success;
  },
);

/** On success, removes the applicant from the local list rather than patching its status to
 * Promoted — the list only ever holds Submitted/Discarded rows (Promoted is never requested),
 * so a promoted applicant just needs to disappear from view. The outcome-specific confirmation
 * text is stashed in `promoteSuccessMessage` for Employees.tsx to show once this modal closes. */
const promoteSuccessMessageKey = (response: IPromoteApplicantResponse): string => {
  if (response.outcome === 'NewInvitation') return APPLICANTS__PROMOTE_SUCCESS_NEW_INVITATION;
  if (response.outcome === 'InvitationReused') return APPLICANTS__PROMOTE_SUCCESS_INVITATION_REUSED;
  return response.skippedLocationIds.length === 0
    ? APPLICANTS__PROMOTE_SUCCESS_BRANCHES_APPENDED
    : APPLICANTS__PROMOTE_SUCCESS_PARTIAL_OVERLAP;
};

export const promoteApplicantAction = createAsyncThunk(
  JOB_APPLICANTS__PROMOTE_APPLICANT,
  async (params: { companyId: number; id: number }, thunkApi): Promise<boolean> => {
    thunkApi.dispatch(setApplicantActionLoadingAction(true));
    thunkApi.dispatch(setApplicantActionErrorAction(null));
    let success = false;
    try {
      const response = await promoteApplicantRequest(params.companyId, params.id);
      thunkApi.dispatch(setPromoteSuccessMessageAction(promoteSuccessMessageKey(response)));
      thunkApi.dispatch(removeApplicantAction({ id: params.id }));
      success = true;
    } catch (error) {
      logError(error);
      const { key } = parseJobApplicantError(error);
      thunkApi.dispatch(setApplicantActionErrorAction(key ?? GENERAL__ERROR_MESSAGE));
    }
    thunkApi.dispatch(setApplicantActionLoadingAction(false));
    return success;
  },
);

export const downloadApplicantCvAction = createAsyncThunk(
  JOB_APPLICANTS__DOWNLOAD_CV,
  async (params: { companyId: number; applicantId: number }, thunkApi) => {
    thunkApi.dispatch(downloadApplicantCvErrorAction(false));
    try {
      const response = await downloadApplicantCvRequest(params.companyId, params.applicantId);
      const urlCreator = window.URL || window.webkitURL;
      const blob = new Blob([response.content], { type: response.contentType });
      const url = urlCreator.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      logError(error);
      thunkApi.dispatch(downloadApplicantCvErrorAction(true));
    }
  },
);
